/**
 * Die deutschen Texte — die **einzige** Stelle, an der sichtbare Zeichenketten stehen
 * (`CLAUDE.md` Regel 6).
 *
 * **Interne Identifier bleiben englisch und stabil**, die Anzeigenamen stehen hier: der
 * Schlüssel ist `flagBearer`, der Text ist „Fähnrich". Niemals den deutschen Namen als
 * Schlüssel oder Enum-Wert benutzen — sonst wandert eine Übersetzung in die RuleConfig, in die
 * Datenbank und in die Protokolle.
 *
 * **Die Fehlertexte sind nach Code sortiert, nicht nach Herkunft.** Drei Codes teilen sich HTTP
 * und WebSocket (`UNAUTHENTICATED`, `RATE_LIMITED`, `INTERNAL`) und bedeuten dort dasselbe; zwei
 * gehören nur dem Client (`NETWORK`, `MALFORMED`). Ein Test prüft, dass **jeder** Code aus
 * `@flaghex/contracts` hier einen Text hat und keiner übrig ist — eine Lücke fiele sonst erst
 * dem Testspieler auf, als Schlüssel auf dem Bildschirm.
 *
 * **Ein Fehlertext sagt, was der Spieler tun kann**, wo das möglich ist. „Nicht berechtigt" ist
 * wahr und nutzlos; „Der Link ist abgelaufen — frag den Host nach einem neuen" ist beides.
 */

export const de = {
  common: {
    appName: 'flaghex',
    /** Der Untertitel auf dem Startbildschirm. Arbeitstitel, kein endgültiger Name. */
    tagline: 'Taktisches Hex-Strategiespiel für zwei bis sechs Personen',
    loading: 'Wird geladen …',
    retry: 'Erneut versuchen',
    cancel: 'Abbrechen',
  },

  start: {
    /**
     * Die Startseite für Verlage (`W2`, **E135**): gestaltet wie die Titelkarte des Erklärfilms,
     * Anrede „du“ wie im Spiel (Entscheidung des User).
     */
    title: 'Flaghex',
    tagline: 'Ein taktisches Strategiespiel auf dem Sechseckbrett',
    keyLabel: 'Zugangsschlüssel',
    keyPlaceholder: 'Schlüssel eingeben',
    unlock: 'Anleitung öffnen',
    keyHint: 'Mit dem Schlüssel aus deiner Einladung öffnest du Spielanleitung und Erklärfilm.',
    keyInvalid: 'Dieser Schlüssel ist nicht gültig. Prüfe die Schreibweise oder frag nach einem neuen.',
    /** Was gezählt wird (`W3`, **E136**) — dieselbe Aussage steht unter dem Film. */
    board: 'Das Flaghex-Brett mit den sechs Fähnrichen',
    /** Der Startbildschirm ohne Link: bewusst keine Partieliste (§65). */
    noLink: 'Mitspielen geht nur über einen Einladungslink.',
  },

  /**
   * Die Admin- und Regeloberfläche (Zyklus 10d).
   *
   * **Die Beschriftungen sind nach Art benannt, nicht nach Pfad** (`labels.movementMin`, nicht
   * `labels.pieces.guard.movement.min`): „Bewegung, mindestens" heißt bei jeder Figurenklasse
   * dasselbe, und fünf gleichlautende Texte wären fünf Stellen, an denen eine Formulierung
   * abweichen kann. Welcher Pfad welche Beschriftung bekommt, steht in `admin/fields.ts`; ein Test
   * hält fest, dass jedes Feld eine hat und keine übrig ist.
   */
  admin: {
    heading: 'Regeln und Host-Links',
    intro:
      'Diese Seite ändert die globalen Balancewerte und verwaltet die Host-Links. Beides ist dem Admin vorbehalten.',

    key: {
      heading: 'Admin-Schlüssel',
      intro:
        'Diese Oberfläche braucht den Admin-Schlüssel des Dienstes. Er steht in keiner Adresse und wird eingegeben.',
      label: 'Admin-Schlüssel',
      submit: 'Öffnen',
      /**
       * Der Dienst antwortet auf fehlenden und falschen Schlüssel **absichtlich gleich** (wer
       * rät, soll nichts dazulernen). Diese Oberfläche kann die Fälle deshalb nicht
       * unterscheiden — also nennt der Text beide, statt einen zu behaupten.
       */
      rejected:
        'Der Schlüssel wurde abgewiesen. Entweder stimmt er nicht — oder der Dienst hat gar keinen konfiguriert; dann ist diese Oberfläche geschlossen, und das ist Absicht.',
      storageHint:
        'Der Schlüssel bleibt nur in diesem Tab und ist vergessen, sobald du ihn schließt.',
      forget: 'Schlüssel vergessen',
    },

    rules: {
      heading: 'Globale Regelkonfiguration',
      /** §53: der Snapshot ist der Grund, warum Schrauben an laufenden Partien nichts tut. */
      intro:
        'Gilt für neu erstellte Partien. Laufende Partien behalten die Werte, mit denen sie angelegt wurden.',
      save: 'Speichern',
      saved: 'Gespeichert. Neue Partien benutzen ab jetzt diese Werte.',
      discard: 'Änderungen verwerfen',
      unchanged: 'Keine Änderungen.',
      changed: 'Ungespeicherte Änderungen.',
      /** Die Antwort des Dienstes nennt bewusst keinen zod-Pfad — darum wird sie mitgezeigt. */
      rejected: 'Der Dienst hat diese Konfiguration abgewiesen:',
      unlimited: 'unbegrenzt',
      unlimitedHint: 'leer = unbegrenzt',
      sameAsMovement: 'so weit wie die Bewegung',
      /** Die beiden Zahlen einer Reichweite. Nicht in `labels`: sie gehören keinem Feld. */
      min: 'mindestens',
      max: 'höchstens',
    },

    groups: {
      general: 'Allgemein',
    },

    labels: {
      version: 'Fassung dieser Regeln',
      sideLength: 'Brett-Kantenlänge',
      startZoneRows: 'Startreihen je Sektor',
      scoringTarget: 'Punkteziel',
      resetScoreOnFlagCapture: 'Punkte zurücksetzen, wenn der Fähnrich geschlagen wird',
      setupTimeLimitMs: 'Aufstellungszeit in Millisekunden',
      respawnEnabled: 'Respawn geschlagener Figuren',
      ringEnabled: 'Ringfelder aktiv',
      goalCellPassage: 'Zielfeld durchqueren',
      botsMinDeliberationMs: 'Erwägungsdauer eines Bots in Millisekunden',
      count: 'Anzahl',
      movementMin: 'Bewegung, mindestens',
      movementMax: 'Bewegung, höchstens',
      canJump: 'Darf Figuren überspringen',
      capture: 'Schlagreichweite',
      pushRangeMin: 'Stößt aus Entfernung, mindestens',
      pushRangeMax: 'Stößt aus Entfernung, höchstens',
      pushDistance: 'Schubweite',
      pusherAdvance: 'Der Stoßer rückt nach',
      canPushOffBoard: 'Darf über den Brettrand stoßen',
      canPushFlagBearerFromGoal: 'Darf den Fähnrich aus dem Zielfeld stoßen',
    },

    passage: {
      open: 'offen für alle',
      jumpersOnly: 'nur für Springer',
      blocked: 'gesperrt',
    },

    /** Die Verlagsschlüssel (`W2`, **E135**) — sie öffnen die Anleitung samt Erklärfilm. */
    publisherKeys: {
      heading: 'Verlagsschlüssel',
      intro:
        'Ein Verlagsschlüssel öffnet auf der Startseite die Spielanleitung samt Erklärfilm, sonst nichts. Ein Schlüssel je Verlag; ein Widerruf sperrt sofort.',
      label: 'Verlag',
      create: 'Schlüssel anlegen',
      none: 'Noch kein Verlagsschlüssel angelegt.',
      createdHeading: 'Dieser Schlüssel erscheint nur jetzt',
      createdHint:
        'Gespeichert ist nur sein Fingerabdruck. Kopiere ihn jetzt und gib ihn dem Verlag zusammen mit der Adresse dieser Seite.',
      createdAt: 'angelegt am {{when}}',
      lastUsedAt: 'zuletzt benutzt am {{when}}',
      neverUsed: 'noch nicht benutzt',
      revoke: 'Widerrufen',
      revoked: 'widerrufen am {{when}}',
      /** Was ein Verlag mit der Anleitung getan hat (`W3`, **E136**). */
      activity: {
        none: 'noch nicht freigeschaltet',
        unlocks: '{{count}}× freigeschaltet, zuletzt {{when}}',
        opens: '{{count}}× geöffnet',
        film: 'Film {{count}}× gestartet',
        filmFurthest: 'Film {{count}}× gestartet, bis {{at}} von {{of}}',
        read: 'gelesen bis Kapitel {{n}} von {{of}}',
      },
      history: {
        show: 'Verlauf zeigen',
        hide: 'Verlauf ausblenden',
        empty: 'Noch nichts geschehen.',
        UNLOCK: 'freigeschaltet',
        OPEN: 'Anleitung geöffnet',
        FILM_START: 'Film gestartet',
        FILM_PROGRESS: 'Film bis {{at}}',
        FILM_CHAPTER: 'Kapitelsprung auf {{at}}',
        READ_PROGRESS: 'gelesen bis Kapitel {{n}}',
      },
    },
    hostLinks: {
      heading: 'Host-Links',
      intro:
        'Ein Host-Link erlaubt, eine Partie anzulegen — eine zur Zeit. Er gilt wiederverwendbar: ist die Partie vorbei, legt derselbe Link die nächste an.',
      label: 'Bezeichnung',
      labelHint: 'Damit nachvollziehbar bleibt, wer welchen Link bekommen hat.',
      create: 'Host-Link anlegen',
      none: 'Noch kein Host-Link angelegt.',
      /** Der Klartext erscheint genau einmal — danach existiert nur noch sein Hash. */
      createdHeading: 'Dieser Link erscheint nur jetzt',
      createdHint:
        'Gespeichert ist nur sein Fingerabdruck. Kopiere ihn jetzt — ein verlorener Link wird ersetzt, nicht wiederhergestellt.',
      createdAt: 'angelegt am {{when}}',
      lastUsedAt: 'zuletzt benutzt am {{when}}',
      neverUsed: 'noch nicht benutzt',
      revoke: 'Widerrufen',
      revoked: 'widerrufen am {{when}}',
    },
  },

  /**
   * Die Partiestatus aus §47.
   *
   * Eigener Namensraum wie `pieces` und `sectors`, und aus demselben Grund: die internen Werte
   * bleiben englisch und stabil (sie stehen in der Datenbank und in jedem Protokoll), die
   * Anzeigenamen stehen hier. Ein Test prüft sie gegen `GAME_STATUSES` aus `@flaghex/contracts`.
   */
  status: {
    CREATED: 'Angelegt',
    LOBBY: 'Lobby offen',
    SETUP: 'Aufstellung läuft',
    PLAYING: 'Partie läuft',
    PAUSED: 'Pausiert',
    FINISHED: 'Beendet',
    ABORTED: 'Abgebrochen',
  },

  /**
   * Partieerstellung, Einladung, Beitritt und Lobby (Zyklus 10e) — §67 Schritte 4 bis 18.
   *
   * **Eine Form, zwei Ansichten** (Nr. 27): der Host sieht Konfiguration und Lobby in einem,
   * der Mitspieler dieselbe Lobby ohne Bedienelemente. Die Texte sind entsprechend geteilt —
   * was beide sehen, steht nicht zweimal da.
   */
  lobby: {
    heading: 'Partie',
    hostHeading: 'Partie einrichten',
    playerHeading: 'Lobby',
    /**
     * Die Überschrift, sobald die Partie läuft — für **beide** Bildschirme dieselbe.
     *
     * Bis Zyklus 10g2 stand dort weiter „Partie einrichten" beziehungsweise „Lobby", obwohl auf
     * demselben Bildschirm gezogen wurde. In der Sichtprüfung sofort zu sehen, von keinem Test
     * bemerkt: beide Texte waren ja vorhanden und richtig geschrieben.
     */
    playingHeading: 'Deine Partie',
    statusLabel: 'Stand: {{status}}',
    reload: 'Neu laden',

    create: {
      heading: 'Neue Partie',
      intro:
        'Der Ersteller ist Host und spielt mit. Alles hier bleibt bis zum Start veränderbar — die Lobby füllt sich, während du noch konfigurierst.',
      mode: 'Modus',
      playerCount: 'Teilnehmerzahl',
      slots: 'Farben und Teams',
      slotSector: 'Farbe',
      slotTeam: 'Team',
      noTeam: 'kein Team',
      teamHint:
        'Teams sind frei wählbar — auch ungleich, etwa zwei Anfänger gegen einen erfahrenen Spieler.',
      sectorHint:
        'Die Startsektoren wählst du frei; der Vorschlag verteilt sie gleichmäßig. Bei fünf Spielern geht das nicht auf.',
      hostName: 'Dein Anzeigename',
      hostSector: 'Deine Farbe',
      victory: 'Siegbedingung',
      /** Rahmentitel — nicht derselbe Text wie das Feld darin, das las sich wie ein Fehler. */
      victoryHeading: 'Partieende und Zeiten',
      hostHeading: 'Du als Host',
      pointTarget: 'Punkteziel',
      timeBudget: 'Bedenkzeit je Spieler in Minuten',
      setupTime: 'Aufstellungszeit in Minuten',
      setupTimeHint: 'leer = unbegrenzt',
      resetScore: 'Punkte zurücksetzen, wenn der Fähnrich geschlagen wird',
      showCoordinates: 'Feldkoordinaten einblenden',
      showCoordinatesHint:
        'Hilft, eine Stellung zu besprechen — in den Modi 2:2:2 und 3:3 können Mitspieler sich ohne gemeinsame Benennung nicht abstimmen. Das ist der Startwert für alle; im Spiel schaltet jeder sie am Brett selbst um.',
      overrideHint:
        'Punkteziel und Score-Reset sind mit der globalen Regelkonfiguration vorbelegt.',
      submit: 'Partie erstellen',
      failed: 'Die Partie ließ sich nicht erstellen:',
    },

    /** Die vier Modi aus §8 — Voreinstellungen, keine abgeschlossene Liste. */
    modes: {
      duel: '1:1',
      freeForAll: 'Jeder gegen jeden',
      teams222: '2:2:2',
      teams33: '3:3',
    },

    victory: {
      POINTS: 'Punktsieg',
      TIME: 'Zeitsieg',
      pointsHint: 'Wer das Punkteziel zuerst erreicht, gewinnt.',
      timeHint: 'Wessen Bedenkzeit abläuft, scheidet aus. Eine Bedenkzeit ist dafür nötig.',
    },

    invite: {
      heading: 'Einladungslink',
      hint: 'Schick ihn deinen Mitspielern. Wer ihn hat, darf beitreten — er ist die Berechtigung.',
      once:
        'Er erscheint nur einmal und wird in diesem Browser behalten. Geht er verloren, erzeugst du einen neuen; der alte verfällt dabei.',
      missing:
        'Der Einladungslink dieser Partie liegt in diesem Browser nicht vor. Erzeuge einen neuen — der alte verfällt dabei.',
      renew: 'Neuen Einladungslink erzeugen',
    },

    slots: {
      heading: 'Farbplätze',
      free: 'frei',
      team: 'Team {{team}}',
      disconnected: 'nicht verbunden',
      /**
       * Das Abzeichen eines Bots (**E64**, Antwort 2) — und es steht **statt** der
       * Verbindungsanzeige, nicht neben ihr.
       *
       * Ein Bot hat nie einen WebSocket: `connected` ist für ihn immer `false`. In der
       * Sichtprüfung von `V2-2e3` stand deshalb „Robo · Grün · nicht verbunden", **während er
       * zog** — die Anzeige war nicht überflüssig, sondern falsch.
       */
      bot: 'Bot',
    },

    waiting: {
      heading: 'Ohne Farbe in der Lobby',
      none: 'Niemand wartet ohne Farbe.',
      hint: 'Wer ohne Farbe beigetreten ist, bekommt sie vom Host — oder wählt selbst.',
    },

    host: {
      assign: 'Zuordnen',
      unassign: 'Farbe lösen',
      remove: 'Entfernen',
      /**
       * Einen Bot einsetzen (**E64**, Antwort 1): am **freien Farbplatz**, dort wo „Zuordnen",
       * „Farbe lösen" und „Entfernen" schon stehen.
       *
       * **Den Namen tippt der Host** (Antwort 3) und er wird nicht aus der Farbe erzeugt: nur
       * so sind zwei Konfigurationen derselben Strategie unterscheidbar — `V2 aggressiv` gegen
       * `V1 flach` —, und genau das vergleicht das Balancing-Labor später. Der Ein-Klick-Weg
       * wird damit zu *klicken, tippen, bestätigen*; das ist der bewusst gezahlte Preis.
       */
      botName: 'Name des Bots',
      addBot: 'Bot einsetzen',
      start: 'Partie starten',
      close: 'Partie schließen',
      closeConfirm: 'Wirklich schließen?',
      cannotStart:
        'Starten geht erst, wenn jeder Farbplatz belegt ist und mindestens zwei Seiten übrig bleiben.',
      started: 'Die Partie läuft — die Aufstellung hat begonnen.',
      failed: 'Das hat der Dienst abgewiesen:',
    },

    /**
     * Die Wahl eines Bots am freien Platz (`L2`, **E104**, **E110**). Die Kennungen bleiben
     * englisch (`easy`, `alphaBeta`, `mobility` — Regel 6), die Namen stehen hier.
     *
     * **Die Namen der Gewichte sind Claudes Vorschlag** (E110 ließ sie offen, eine Textfrage):
     * sie beschreiben, was das Merkmal **misst**, nicht wie es rechnet.
     */
    // Seit `L4` (**E127**) wählt die Lobby nur die Stufe; Strategien, Gewichte und das
    // Sprachmodell braucht noch das **Abzeichen** — für Bots, die über die API eingesetzt wurden.
    bot: {
      level: 'Stufe',
      levels: { easy: 'leicht', medium: 'mittel', strong: 'stark' },
      strategies: {
        alphaBeta: 'Alpha-Beta',
        maxN: 'MaxN',
        greedy: 'Greedy',
        random: 'Zufall',
        llm: 'Sprachmodell',
      },
      weights: {
        points: 'Punkte',
        nearVictory: 'Nähe zum Sieg',
        flagProximity: 'Fähnrich nah am Ziel',
        goalHeld: 'Ziel gehalten',
        mobility: 'Beweglichkeit',
        offBoard: 'geschlagene Figuren',
        ringControl: 'Ringkontrolle',
        threatened: 'bedrohte Figuren',
        flagThreatened: 'bedrohter Fähnrich',
        intruders: 'Eindringlinge in der Startzone',
      },
      /**
       * Das Abzeichen und sein ⓘ (`L3a`, **E104** Teilfrage 3, **E112**) — in Lobby **und** Spiel,
       * deshalb hier und nicht zweimal. Kein Preis, keine Kosten (E112, 3).
       */
      badge: {
        resolvesAtStart: 'Strategie beim Start: Alpha-Beta, Tiefe 3 — ab drei Seiten paranoid',
        depth: 'Tiefe {{depth}}',
        provider: 'Anbieter {{id}}',
        model: 'Modell {{id}}',
        infoLabel: 'Einstellungen des Bots',
        /**
         * Der Rückfall eines LLM-Bots (`L3b`, **E112** Antwort 2): wie oft er als Greedy gezogen
         * hat, und warum zuletzt. Die Gründe gelten auch in der Historie.
         */
        fallbacks_one: '{{count}} Zug als Greedy',
        fallbacks_other: '{{count}} Züge als Greedy',
        fallbacksLast: '(zuletzt: {{reason}})',
        reasons: {
          killSwitch: 'Not-Aus',
          gameCap: 'Partiedeckel',
          dayCap: 'Tagesdeckel',
          unpriced: 'kein Preis',
          timeout: 'Frist überschritten',
          providerError: 'Fehler beim Anbieter',
          notOffered: 'ungültiger Zug',
          invalidPlacement: 'ungültige Aufstellung',
        },
      },
    },

    join: {
      heading: 'Mitspielen',
      intro: 'Gib einen Namen ein und wähl eine freie Farbe. Beides siehst du gleich in der Lobby.',
      name: 'Dein Anzeigename',
      sector: 'Deine Farbe',
      noSector: 'noch keine — später wählen',
      noSectorHint:
        'Du darfst auch ohne Farbe warten; der Host kann dir dann eine zuordnen.',
      submit: 'Beitreten',
      failed: 'Der Beitritt hat nicht geklappt:',
      waitingForStart: 'Warte auf den Host — er startet die Partie.',
    },
  },

  board: {
    /**
     * Die Zoombedienung (§64). Ein Feld ist bei 420 Pixeln Breite rund 13 Pixel breit — ohne
     * Zoom ist das Brett auf einem Telefon nicht bedienbar.
     */
    zoomIn: 'Näher heran',
    zoomOut: 'Weiter weg',
    zoomReset: 'Ganzes Brett',
    zoomLevel: '{{percent}} %',

    /** Der Name des Bretts für Vorlesewerkzeuge. */
    name: 'Spielbrett',
    /** Die Beschriftung eines einzelnen Feldes. Die ID ist bewusst darin — sie ist die Adresse. */
    cell: 'Feld {{id}}',
    showCoordinates: 'Feldkoordinaten anzeigen',
    /** Der Name einer Figur für Vorlesewerkzeuge — Klasse und Farbe, sonst ist sie nicht zu unterscheiden. */
    piece: '{{piece}} ({{sector}})',
    /** Dieselbe Figur als **erwogener** Zug (§36) — ohne diesen Zusatz stünde sie doppelt da. */
    previewPiece: '{{piece}} ({{sector}}), erwogener Zug',
    coveredPiece: 'Verdeckte Figur ({{sector}})',
    /** Die Beschriftung des zentralen Zielfeldes — wie in der Referenzgrafik, immer sichtbar. */
    goalLabel: 'Ziel',
    selected: 'Gewähltes Feld: {{id}}',
    nothingSelected: 'Kein Feld gewählt',
  },

  /**
   * Die verdeckte Aufstellung (Zyklus 10f, seit `R17` verdeckt statt geheim) — §10, §11, §67
   * Schritte 19 bis 23, Nr. 55.
   *
   * **Was sichtbar ist, sagen die Texte ausdrücklich**: jeder sieht, *wo* die anderen stehen, nie
   * *welche* Figur (Nr. 55). Ein Spieler, der glaubt, seine Aufstellung sei unsichtbar, stellt
   * anders auf — und umgekehrt.
   */
  setup: {
    heading: 'Aufstellung',
    intro:
      'Setz deine Figuren in deine Startzone. Die anderen sehen, wo sie stehen, aber nicht, welche — bis alle fertig sind.',
    pool: 'Dein Vorrat',
    poolEmpty: 'Alle Figuren stehen.',
    selectHint: 'Wähl eine Figur, dann ein hervorgehobenes Feld.',
    placedHint: 'Eine gesetzte Figur anklicken: dann versetzen oder zurücknehmen.',
    unplace: 'Zurück in den Vorrat',
    confirm: 'Aufstellung bestätigen',
    confirmed: 'Du hast bestätigt — deine Aufstellung steht fest.',
    incomplete: 'Bestätigen geht erst, wenn jede Figur steht.',
    progress: 'Stand der anderen',
    progressEntry: '{{placed}} von {{total}} Figuren',
    progressConfirmed: 'bestätigt',
    secret: 'Von den anderen siehst du, wo ihre Figuren stehen — aber nicht, welche.',
    deadline: 'Noch {{time}}',
    deadlineNone: 'Ohne Zeitbegrenzung.',
    deadlineOver: 'Die Zeit ist abgelaufen — fehlende Figuren werden gleich zufällig verteilt.',
  },

  /** Die laufende Partie: Zug, Erwägung, Stand, Figureninfo, Historie und Partieende. */
  play: {
    heading: 'Partie',
    abort: 'Partie beenden',
    abortConfirm: 'Wirklich beenden?',
    /** Der Schalter am Brett (`R14`) — wirkt nur im eigenen Browser. */
    coordinates: 'Koordinaten',
    /** Das Ergebnis als Karte über dem Brett (`R13`) — das Urteil aus Sicht des Betrachters. */
    result: {
      won: 'Gewonnen!',
      lost: 'Verloren',
      draw: 'Unentschieden',
      over: 'Partie zu Ende',
      aborted: 'Partie beendet',
      abortedBy: 'Der Host hat die Partie beendet. Sie wird nicht gewertet.',
      score: 'Stand {{score}}',
      dismiss: 'Brett ansehen',
    },
    /**
     * Der Satz unter dem Brett — **je Zustand ein eigener** (Befund B3, entschieden am
     * 2026-10-07 vom User). Er erklärt, warum das Brett so aussieht, wie es aussieht, und darf
     * deshalb nicht stehen bleiben, wo er nicht mehr stimmt.
     */
    revealed: 'Die Aufstellung ist vorbei — jetzt sind alle Figuren sichtbar.',
    /**
     * In der Pause verdeckt die Projektion das **gesamte** Brett, für alle (Nr. 26: „die Pause
     * darf keine Analysezeit sein"). Bis R5 stand hier „jetzt sind alle Figuren sichtbar" —
     * nicht ungenau, sondern das Gegenteil des Sichtbaren. Hier ist die Erklärung am nötigsten:
     * ohne sie steht man vor einem leeren Brett ohne Grund.
     */
    boardHidden: 'Das Brett ist während der Pause verdeckt.',
    /**
     * **Wessen Figur getroffen wurde** (Befund B6, entschieden am 2026-10-07 vom User).
     *
     * Seit **R2** stößt der Stoßer jede Figur, auch die eigene — der Besitz des Opfers ist damit
     * keine Selbstverständlichkeit mehr. Vorher war das Opfer immer gegnerisch, und „Stoßer
     * stößt Wächter" sagte alles.
     *
     * **Die Grammatik steht hier und nicht im Code.** Ein zusammengesetztes `name + 's'` im Code
     * wäre ein deutscher Genitiv an einer Stelle, die keine Sprache kennen darf — eine andere
     * Übersetzung ordnet anders („Bert's runner", „le coureur de Bert"). Die **Auswahl** zwischen
     * den beiden Formen ist dagegen Logik und steht im Code.
     */
    ownedPiece: '{{owner}}s {{piece}}',
    /** Für Namen auf s, ß, x oder z: „Hans' Läufer", nicht „Hanss Läufer". */
    ownedPieceSibilant: "{{owner}}' {{piece}}",

    /** Wer am Zug ist (§50). Die Zugnummer steht dabei, weil sie die Runde einordnet. */
    yourTurn: 'Zug {{turn}} — du bist am Zug',
    turnOf: 'Zug {{turn}} — {{name}} ist am Zug',
    paused: 'Die Partie ist pausiert.',
    over: 'Die Partie ist beendet.',

    selectHint: 'Wähl eine eigene Figur, dann ein hervorgehobenes Feld.',
    waitHint: 'Du bist nicht am Zug — zusehen, was der andere erwägt.',

    /**
     * Die Reiter der Schmalform (§64, Zyklus D2d). Auf breiten Bildschirmen stehen alle
     * Bereiche untereinander und die Reiter sind ausgeblendet — kurz heißen sie trotzdem,
     * weil vier davon in eine Zeile von 420 Pixeln passen müssen.
     */
    tab: {
      turn: 'Zug',
      setup: 'Aufstellen',
      piece: 'Figur',
      status: 'Punkte',
      history: 'Historie',
    },

    actions: 'Mögliche Züge',
    actionsHint: 'Ein Zug besteht aus genau einer dieser Aktionen.',
    noActions: 'Diese Figur kann nichts tun.',
    nothingSelected: 'Keine Figur gewählt.',

    pool: 'Geschlagene Figuren',
    poolEmpty: 'Keine geschlagenen Figuren.',
    poolHint: 'Eine geschlagene Figur kommt in die eigene Startzone zurück — das ist ein ganzer Zug.',

    /** Die öffentliche Preview (§29, §36). Jeder sieht sie, nur einer kann sie bestätigen. */
    preview: 'Erwogener Zug',
    previewNone: 'Gerade erwägt niemand etwas.',
    previewBy: '{{name}} erwägt:',
    previewMine: 'Du erwägst:',
    commit: 'Zug bestätigen',
    cancel: 'Erwägung zurücknehmen',
    /**
     * **Der kürzere Weg steht hier und nicht auf dem Brett** (Zyklus R7).
     *
     * Der User hat den zweiten Klick **ohne Zeichen** entschieden — kein Haken auf dem Schatten,
     * kein schwebender Dialog. Dann muss es wenigstens einmal dastehen, sonst findet es niemand;
     * und diese Zeile steht ohnehin direkt unter den beiden Knöpfen, also genau dort, wo man
     * gerade hinsieht.
     *
     * **Die Warnung bleibt die zweite Hälfte**, nicht die erste: dass Bestätigen endgültig ist,
     * gilt für beide Wege — und der neue ist der schnellere, also der, bei dem sie zählt.
     */
    commitHint:
      'Nochmal auf das Zielfeld tippen bestätigt auch. Bestätigen ist endgültig — es gibt kein Zurück.',

    /** Der Statusbereich nach §49: wer mitspielt, mit Farbe, Punkten, Zeit und Vorrat. */
    standing: 'Stand',
    activeMark: 'am Zug',
    /**
     * Das Abzeichen „regelwidrig erzeugt" und sein Satz (Zyklus 11g, Nr. 38).
     *
     * **Der Satz nennt die Folge, nicht den Zustand.** „Achtung" oder „verändert" wüsste in drei
     * Tagen niemand mehr zu deuten; was zählt, ist: aus dieser Partie lässt sich nichts über das
     * Spiel lernen. Genau dieser Irrtum ist der Schaden, den Nr. 38 verhindern will.
     */
    ruleOverridden: 'regelwidrig erzeugt',
    ruleOverriddenHint:
      'In dieser Partie wurde eine Stellung erzwungen, die es im Spiel nicht gibt — sie zählt nicht als Balancing-Daten. Was dabei übergangen wurde, steht in der Zughistorie.',
    offline: 'nicht verbunden',
    eliminated: 'ausgeschieden',
    /**
     * Der Vorlesename am Abzeichen eines Bots in der Spielerübersicht (**E64**, Antwort 2). Das
     * Etikett selbst kommt seit `L3a` aus `lobby:slots.bot` und `lobby:bot.*` — Lobby und Spiel
     * zeigen dasselbe (**E104** Teilfrage 3); hier steht nur der ganze Satz fürs `aria-label`.
     *
     * **Es steht statt der Verbindungsanzeige**, nicht neben ihr: `connected` ist für einen Bot
     * immer `false`, und „nicht verbunden" war in der Sichtprüfung von `V2-2e3` zu lesen,
     * während der Bot zog.
     */
    botPlayer: 'Dieser Platz wird von einem Bot gesteuert',
    /**
     * Singular und Plural, gezählt über `count` — so verlangt es i18next, und so bekommt jede
     * weitere Sprache ihre eigenen Regeln statt einer eingebauten deutschen Annahme. „1 Punkte"
     * stand in der Sichtprüfung zu Zyklus 10i im Statusbereich.
     */
    score_one: '{{count}} Punkt',
    score_other: '{{count}} Punkte',
    sideScore: 'Team {{team}}: {{score}}',
    teamName: 'Team {{team}}',
    time: 'Bedenkzeit {{time}}',
    poolOf: 'Vorrat: {{pieces}}',
    poolNone: 'Vorrat: leer',
    pointTarget: 'Punkteziel: {{target}}',
    turnNumber: 'Zug {{turn}}',

    /**
     * Die Figureninformation (§51): Name, Besitzer, Team, Bewegung, Schlagreichweite,
     * Sonderfähigkeit. Die **Werte** kommen aus der RuleConfig der Partie, nicht von hier.
     */
    info: {
      heading: 'Gewählte Figur',
      owner: 'Gehört {{name}}',
      movement: 'Bewegung: {{range}}',
      capture: 'Schlag: {{range}}',
      noCapture: 'Schlag: kein normaler Schlag',
      /** Eine Reichweite in drei Formen — fest, von–bis, und unbegrenzt (Default des Läufers). */
      rangeExact: '{{min}}',
      rangeSpan: '{{min}}–{{max}}',
      rangeUnlimited: 'ab {{min}}, unbegrenzt',
      jump: 'Kann Figuren überspringen.',
      push: 'Stößt aus {{range}} Feldern Entfernung und schiebt {{distance}} weit.',
      pushStays: 'Bleibt dabei stehen.',
      pushAdvance: 'Rückt dabei {{advance}} nach.',
      pushOffBoard: 'Kann über den Brettrand stoßen — die Figur gilt dann als geschlagen.',
      pushFromGoal:
        'Kann einen Fähnrich aus dem Zielfeld stoßen — die Punkte seiner Seite bleiben dabei erhalten.',
      flagBearer:
        'Betritt als einzige Figur das Zielfeld und punktet dort. Schlägt nur dort, und nur einen gegnerischen Fähnrich.',
    },

    /**
     * Die Zughistorie (§56, §67 Schritt 47) — ein Text je permanentem Ereignistyp.
     *
     * Vollständigkeit prüft ein Test gegen `PERMANENT_EVENT_TYPES` aus `@flaghex/contracts`, wie
     * bei den Fehlercodes: ein neuer Ereignistyp fällt damit im Test auf und nicht dem
     * Testspieler als Schlüssel auf dem Bildschirm.
     */
    history: {
      heading: 'Zughistorie',
      empty: 'Noch nichts geschehen.',
      line: '{{turn}}. {{who}}: {{what}}',
      /**
       * Ein Zug, den ein LLM-Bot als Greedy gemacht hat (`L3b`, **E112** Antwort 2) — hinter der
       * Zeile, mit dem Grund aus `lobby:bot.badge.reasons`.
       */
      fallback: '↪ Greedy',
      fallbackWhy: '↪ Greedy ({{reason}})',
      /**
       * Der Handelnde, wenn er ein Bot ist (**E64**, Antwort 4) — und zwar in **jeder** Zeile,
       * nicht nur einmal am Anfang.
       *
       * Das ist redundanter als nötig, mit Absicht: die Historie ist ab V3 Balancing-Material,
       * das **allein** gelesen wird. Eine Kennzeichnung nur am Anfang macht die Mitte einer
       * Aufzeichnung mehrdeutig — und bei einem frei getippten Namen (Antwort 3) ist
       * `V2 aggressiv: Läufer nach purple-F2` von der Zeile eines Menschen nicht zu
       * unterscheiden.
       *
       * **Hier in Klammern und nicht als Abzeichen**: in laufendem Text gibt es keine
       * Zahlenspalte, in der ein Abzeichen stehen könnte, und die Zeile bleibt so eine einzige
       * Zeichenkette — lesbar als Aufzeichnung, und die Zusage aus 10h3 („geprüft wird die
       * ganze Zeile") gilt unverändert.
       */
      botWho: '{{name}} [Bot]',
      lineGame: '{{turn}}. {{what}}',
      MOVE: '{{piece}} nach {{cell}}',
      CAPTURE: '{{piece}} schlägt {{victim}} auf {{cell}}',
      /**
       * **Beide Felder** (Befund B1, entschieden am 2026-10-07 vom User).
       *
       * `{{cell}}` ist, wo das Opfer **steht** — bei einem Stoß wählt der Spieler das Opfer,
       * nicht das Ziel. Bis R5 hieß die Zeile „stößt Fähnrich **auf** yellow-H1", und die
       * Präposition las sich als Ziel: man dachte, die Figur werde dorthin geschoben, wo sie
       * schon steht. Wohin sie geschoben wird, ist beim Stoß der ganze Punkt — so trägt man
       * den Fähnrich ins Zentrum und wirft Figuren über den Rand.
       */
      PUSH: '{{piece}} stößt {{victim}} von {{cell}} nach {{target}}',
      /**
       * Ein Randwurf ist etwas anderes als eine Verschiebung — die Figur ist danach weg (§18).
       * Und er hat **kein** Landefeld, also auch kein `{{target}}`: ein leeres Feld hinter
       * „nach" wäre schlechter als ein eigener Satz.
       */
      PUSH_OFF: '{{piece}} stößt {{victim}} von {{cell}} über den Rand',
      RESPAWN: '{{piece}} eingesetzt auf {{cell}}',
      SCORE: 'Punkt — Stand {{score}}',
      FLAG_CAPTURE: 'Fähnrich im Zentrum geschlagen — Punkte zurückgesetzt',
      AUTO_SETUP: 'Fehlende Figuren zufällig verteilt',
      TIMEOUT: 'Bedenkzeit abgelaufen — ausgeschieden',
      GAME_STARTED: 'Die Partie beginnt',
      /**
       * Der allgemeine Satz — der **Rückfall** (Befund B2, R5).
       *
       * Er gilt für ein Ereignis, das den Ausgang nicht mitführt: eine Partie aus einer Zeit vor
       * R5. Lieber ein knapper wahrer Satz als „undefined gewinnt" — dieselbe Vorsicht, mit der
       * diese Schicht den ganzen Payload liest.
       */
      GAME_FINISHED: 'Die Partie ist beendet',
      /**
       * **Der Ausgang gehört in die Historie, nicht nur in den Stand** (B2, entschieden am
       * 2026-10-07 vom User). Das Ereignis trägt den Sieger seit Phase 6 im Payload; benutzt
       * hat ihn niemand, und so stand über jedem Sieg derselbe Satz wie über jedem Abbruch.
       *
       * **Dieselbe Formulierung wie im Statusbereich** (`play:outcome`): eine Aussage, zwei
       * Orte, sie können nicht auseinanderlaufen. Der Statusbereich zeigt immer nur den letzten
       * Stand — die Historie ist das, was nach einem Neuladen übrig bleibt (§56).
       */
      GAME_FINISHED_WON: '{{winner}} gewinnt — {{reason}}',
      /** Ein Ende ohne Sieger ist seit R1 ein regulärer Ausgang (**E48**), kein Sonderfall. */
      GAME_FINISHED_DRAW: 'Unentschieden — {{reason}}',
      /**
       * Der Debug-Eingriff (§60, **E42**) — **der allgemeine Text und einer je Eingriffsart.**
       *
       * Der allgemeine ist der Rückfall: die Gruppe „Verändern" wächst in den Zyklen 11e bis
       * 11g um weitere Arten, und eine Historie, die davon eine leere Zeile bekäme, wäre für
       * genau die Partien unbrauchbar, für die es sie gibt. Lieber knapp als lückenhaft.
       *
       * **„Eingriff" steht vorn**, nicht hinten: die Zeile muss auf den ersten Blick von einem
       * Zug zu unterscheiden sein (Frage 2 zu §60). Ein Spielername steht nicht darin, weil
       * niemand gezogen hat (Nr. 37) — das erledigt `lineGame`.
       */
      DEBUG_INTERVENTION: 'Debug-Eingriff',
      DEBUG_MOVE_PIECE: 'Debug-Eingriff: {{piece}} auf {{cell}} gesetzt',
      /**
       * Der **erzwungene** Eingriff (Nr. 38) — eigener Satz, nicht ein Zusatz.
       *
       * „Nie stillschweigend": die Antwort auf Nr. 38 verlangt die Markierung „regelwidrig
       * erzeugt" sichtbar in der Oberfläche und nicht nur im Log. Wer zehn Minuten später eine
       * seltsame Stellung sieht, muss hier lesen können, dass sie so nie hätte entstehen können.
       */
      DEBUG_MOVE_PIECE_FORCED:
        'Debug-Eingriff: {{piece}} auf {{cell}} gesetzt — regelwidrig erzwungen',
      /**
       * Die drei Eingriffe aus 11e. **Entfernen und Hinzufügen sind nach Nr. 42 im Regelfall
       * erzwungen** — sie verändern den Figurensatz —, der Weg in den Vorrat dagegen nie: genau
       * das tut jeder Schlag (§22). Deshalb hat er keinen erzwungenen Satz.
       */
      DEBUG_REMOVE_PIECE: 'Debug-Eingriff: {{piece}} aus dem Spiel genommen',
      DEBUG_REMOVE_PIECE_FORCED:
        'Debug-Eingriff: {{piece}} aus dem Spiel genommen — regelwidrig erzwungen',
      DEBUG_ADD_PIECE: 'Debug-Eingriff: {{piece}} auf {{cell}} hinzugefügt',
      DEBUG_ADD_PIECE_FORCED:
        'Debug-Eingriff: {{piece}} auf {{cell}} hinzugefügt — regelwidrig erzwungen',
      DEBUG_POOL_PIECE: 'Debug-Eingriff: {{piece}} in den Vorrat gelegt',
      /**
       * Die drei Eingriffe aus 11f. Sie betreffen **keine Figur**, sondern einen Spieler —
       * `{{subject}}` ist der betroffene, nicht der handelnde (handelnd ist niemand, Nr. 37).
       *
       * **Punkte haben keinen erzwungenen Satz**: ein Punktestand ist nach Nr. 39 immer
       * Klasse A, auch über dem Punkteziel. Bedenkzeit und aktive Person haben einen, weil
       * beide Klasse B erreichen können (Nr. 43, Nr. 44).
       */
      DEBUG_SET_SCORE: 'Debug-Eingriff: Punktestand von {{subject}} auf {{score}} gesetzt',
      DEBUG_SET_REMAINING: 'Debug-Eingriff: Bedenkzeit von {{subject}} auf {{time}} gesetzt',
      DEBUG_SET_REMAINING_FORCED:
        'Debug-Eingriff: Bedenkzeit von {{subject}} auf {{time}} gesetzt — regelwidrig über ' +
        'der Bedenkzeit dieser Partie',
      DEBUG_SET_ACTIVE_PLAYER: 'Debug-Eingriff: {{subject}} ist am Zug',
      /**
       * **Der Satz nennt die Regel** und nicht nur „regelwidrig" (Nr. 38 verlangt genau das):
       * dass ein Ausgeschiedener zieht, erklärt auch, warum gleich wieder Figuren von ihm auf
       * dem Brett stehen können (§22, Nr. 44).
       */
      DEBUG_SET_ACTIVE_PLAYER_FORCED:
        'Debug-Eingriff: {{subject}} ist am Zug — regelwidrig, denn dieser Spieler ist ' +
        'ausgeschieden',
      /**
       * Der simulierte Zug (11g) — der einzige Eingriff, der ein **echter Zug** ist (Nr. 37).
       *
       * **Kein erzwungener Satz:** ein simulierter Zug ist legal oder gar nicht. Die Zeile steht
       * vor dem gewöhnlichen Zugereignis und erklärt es: der Zug ist echt, aber niemand am
       * Bildschirm hat ihn gemacht.
       */
      DEBUG_SIMULATE_MOVE: 'Debug-Eingriff: Zug für {{subject}} ausgeführt',
    },

    /** Das Partieende (§26, §67 Schritte 45 und 46). */
    outcome: '{{winner}} gewinnt — {{reason}}',
    /**
     * Das Unentschieden (Zyklus R1) — ein **eigener** Satz, nicht „niemand gewinnt".
     *
     * Ein Ende ohne Sieger ist in Variante B ein regulärer Ausgang und keine Panne; der Satz
     * muss das so sagen. Und er nennt die **Regel**, nicht nur den Zustand — dieselbe
     * Anforderung wie beim Abzeichen „regelwidrig erzeugt": wer zehn Minuten später auf den
     * Bildschirm sieht, muss verstehen, warum es so ausging.
     */
    draw: 'Unentschieden — {{reason}}',
    reason: {
      POINT_TARGET: 'das Punkteziel ist erreicht',
      LAST_WITH_TIME: 'als einzige Seite mit Bedenkzeit übrig',
      /**
       * Der Grund des Unentschiedenen. „Wer vorn lag" statt eines Namens: wer das war, steht
       * im Stand direkt darüber — und die Seitensumme eines Teams hier noch einmal
       * auszurechnen wäre dieselbe Regel an zwei Stellen.
       */
      LEADER_OUT_OF_TIME: 'wer vorn lag, hatte keine Bedenkzeit mehr',
    },

    /**
     * Die vier Aktionsarten in Worten. `PUSH_OFF` ist eigens da, weil ein Stoß über den Rand
     * etwas anderes ist als eine Verschiebung — die Figur ist danach weg (§18).
     */
    action: {
      MOVE: '{{piece}} ziehen nach {{cell}}',
      CAPTURE: '{{piece}} schlägt {{victim}} auf {{cell}}',
      /** Dieselbe Zusage wie in der Historie (B1) — der Befund galt ausdrücklich beiden. */
      PUSH: '{{piece}} stößt {{victim}} von {{cell}} nach {{target}}',
      PUSH_OFF: '{{piece}} stößt {{victim}} von {{cell}} über den Rand',
      RESPAWN: '{{piece}} einsetzen auf {{cell}}',
    },
  },

  pieces: {
    flagBearer: 'Fähnrich',
    guard: 'Wächter',
    runner: 'Läufer',
    jumper: 'Springer',
    pusher: 'Stoßer',
  },

  /**
   * Die Kürzel auf den Figuren (Zyklus 10f).
   *
   * **Zwei Buchstaben, nicht einer:** „Springer" und „Stoßer" fangen beide mit S an, und ein
   * Brett, auf dem zwei Figurenklassen gleich aussehen, ist unbrauchbar. Eigener Namensraum wie
   * `pieces`, damit ein Test ihn gegen `PIECE_TYPES` prüfen kann.
   */
  pieceShort: {
    flagBearer: 'Fä',
    guard: 'Wä',
    runner: 'Lä',
    jumper: 'Sp',
    pusher: 'St',
  },

  sectors: {
    green: 'Grün',
    yellow: 'Gelb',
    red: 'Rot',
    blue: 'Blau',
    orange: 'Orange',
    purple: 'Violett',
  },

  errors: {
    // --- Eigene Codes des Clients -------------------------------------------------------
    NETWORK: 'Keine Verbindung zum Dienst. Prüfe dein Netz und versuche es erneut.',
    MALFORMED:
      'Der Dienst hat unerwartet geantwortet. Vermutlich laufen Browser und Server auseinander — ein Neuladen hilft oft.',

    // --- Gemeinsam: HTTP und WebSocket --------------------------------------------------
    UNAUTHENTICATED: 'Diese Berechtigung gilt nicht. Bitte den Host um einen neuen Link.',
    RATE_LIMITED: 'Zu viele Aktionen in kurzer Zeit. Einen Moment warten und erneut versuchen.',
    INTERNAL: 'Im Dienst ist ein Fehler aufgetreten.',

    // --- Nur HTTP ----------------------------------------------------------------------
    INVALID_REQUEST: 'Die Anfrage hatte nicht die erwartete Form.',
    FORBIDDEN: 'Diese Berechtigung gilt nicht für diese Partie.',
    NOT_FOUND: 'Diese Partie gibt es nicht mehr.',
    WRONG_STATE: 'Das geht in diesem Spielstand nicht mehr.',
    ACTIVE_GAME_EXISTS:
      'Über diesen Host-Link läuft schon eine Partie. Sie muss erst enden — oder du brauchst einen zweiten Link.',
    LOBBY_FULL: 'Die Lobby ist voll — alle Farbplätze sind belegt.',
    SECTOR_TAKEN: 'Diese Farbe hat sich gerade jemand anders genommen.',
    SLOT_NOT_CONFIGURED: 'Diese Farbe gibt es in dieser Partie nicht.',
    INVALID_CONFIGURATION: 'Mit diesen Einstellungen wäre die Partie nicht spielbar.',
    // Die beiden Klassen aus Nr. 38. Sie entstehen nur an der Debug-Schnittstelle (§60) und
    // erreichen einen Spieler nie — hier stehen sie, weil **jeder** Code einen Text hat (ein
    // Code auf dem Bildschirm wäre das Gegenteil einer Fehlermeldung), und weil ein Debug-UI
    // sie bräuchte.
    IMPOSSIBLE_POSITION: 'Diese Stellung ist nicht möglich — auch nicht im Debug-Modus.',
    RULE_VIOLATION:
      'Das verstößt gegen eine Spielregel. Im Debug-Modus geht es mit einem ausdrücklichen „trotzdem" — die Partie zählt danach nicht mehr für das Balancing.',

    // --- Nur WebSocket ------------------------------------------------------------------
    NOT_YOUR_TURN: 'Du bist nicht am Zug.',
    ILLEGAL_ACTION: 'Dieser Zug ist in dieser Stellung nicht erlaubt.',
    NO_PREVIEW: 'Es gibt keinen erwogenen Zug, den du bestätigen könntest.',
    GAME_NOT_PLAYING: 'Die Partie läuft gerade nicht.',
    GAME_NOT_IN_SETUP: 'Die Aufstellung ist vorbei.',
    SETUP_CONFIRMED: 'Du hast deine Aufstellung bestätigt — sie lässt sich nicht mehr ändern.',
    SETUP_INCOMPLETE: 'Bestätigen geht erst, wenn alle Figuren stehen.',
    INVALID_MESSAGE: 'Der Dienst hat die Nachricht nicht verstanden.',
  },
} as const;

export type Translations = typeof de;
